import type { PDFDocumentLoadingTask, PDFPageProxy, RenderTask } from "pdfjs-dist";
import { loadPdfjs, type Pdfjs } from "./pdfjs";

type TextContent = Awaited<ReturnType<PDFPageProxy["getTextContent"]>>;
type TextLayer = InstanceType<Pdfjs["TextLayer"]>;

/** Trailing debounce for resize-driven re-renders (a drag resize fires per frame). */
const RESIZE_DEBOUNCE_MS = 100;

interface Page {
  proxy: PDFPageProxy;
  text: TextContent;
  wrapper: HTMLDivElement;
  canvas: HTMLCanvasElement;
  textHost: HTMLDivElement;
  task: RenderTask | null;
  textLayer: TextLayer | null;
}

export interface PdfPagesCallbacks {
  /** Page 1 is drawn, so the caller can drop its loading placeholder. */
  onReady: () => void;
  /** Loading pdf.js or parsing the bytes as a PDF failed. */
  onError: () => void;
}

const noop = (): void => {};

/**
 * Draw `file` into `host` with pdf.js, one `<canvas>` and `TextLayer` per page.
 *
 * A `ResizeObserver` on `host` keeps the pages fit to its width. Returns a
 * disposer that cancels in-flight work, destroys the pdf.js document and
 * empties `host`. pdf.js loads lazily through `loadPdfjs()` (see `./pdfjs`).
 *
 * The document is parsed once per call. The caller re-mounts only when `file`
 * changes, never on a layout change; a resize re-runs `renderPass`, which
 * cancels the previous pass's `RenderTask`s and redraws at the new width.
 * `cancel()` is synchronous, so the canvas is free again immediately. Each
 * page wrapper carries `--total-scale-factor`, the page's rendered scale, which
 * the `.textLayer` rules in `styles/pdfTextLayer.css` use to size the text spans.
 *
 * The canvas backing store is scaled by `devicePixelRatio` through the render
 * `transform`, so text stays sharp on high-density screens.
 */
export function mountPdfPages(host: HTMLElement, file: Blob, on: PdfPagesCallbacks): () => void {
  let disposed = false;
  let loadingTask: PDFDocumentLoadingTask | null = null;
  let pages: Page[] = [];
  let observer: ResizeObserver | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let renderedWidth = -1;

  /** Fit every page to `host`'s width, superseding any in-flight pass. Resolves once page 1 is drawn. */
  const renderPass = (pdfjs: Pdfjs): Promise<void> => {
    renderedWidth = host.clientWidth;
    const width = Math.max(1, renderedWidth);
    const dpr = window.devicePixelRatio || 1;
    const drawn = pages.map((page) => {
      page.task?.cancel();
      page.textLayer?.cancel();
      const { proxy, wrapper, canvas, textHost } = page;
      const viewport = proxy.getViewport({ scale: width / proxy.getViewport({ scale: 1 }).width });
      wrapper.style.setProperty("--total-scale-factor", String(viewport.scale));
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      page.task = proxy.render({
        canvas,
        viewport,
        transform: dpr === 1 ? undefined : [dpr, 0, 0, dpr, 0, 0],
      });
      textHost.replaceChildren();
      page.textLayer = new pdfjs.TextLayer({
        textContentSource: page.text,
        container: textHost,
        viewport,
      });
      // A superseded task rejects with RenderingCancelledException. The newer
      // pass owns the canvas, so a settled promise is enough here.
      return Promise.all([page.task.promise, page.textLayer.render()]).then(noop, noop);
    });
    return drawn[0] ?? Promise.resolve();
  };

  void (async () => {
    const pdfjs = await loadPdfjs();
    const data = new Uint8Array(await file.arrayBuffer());
    if (disposed) return;
    loadingTask = pdfjs.getDocument({ data });
    const pdf = await loadingTask.promise;
    const loaded = await Promise.all(
      Array.from({ length: pdf.numPages }, async (_, i) => {
        const proxy = await pdf.getPage(i + 1);
        return { proxy, text: await proxy.getTextContent() };
      }),
    );
    if (disposed) return;
    pages = loaded.map(({ proxy, text }) => createPage(host, proxy, text));
    await renderPass(pdfjs);
    if (disposed) return;
    on.onReady();
    observer = new ResizeObserver(() => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        if (host.clientWidth !== renderedWidth) void renderPass(pdfjs);
      }, RESIZE_DEBOUNCE_MS);
    });
    observer.observe(host);
  })().catch(() => {
    if (!disposed) on.onError();
  });

  return () => {
    disposed = true;
    if (timer !== null) clearTimeout(timer);
    observer?.disconnect();
    for (const page of pages) {
      page.task?.cancel();
      page.textLayer?.cancel();
    }
    void loadingTask?.destroy();
    host.replaceChildren();
  };
}

function createPage(host: HTMLElement, proxy: PDFPageProxy, text: TextContent): Page {
  const wrapper = document.createElement("div");
  wrapper.className = "relative bg-white";
  const canvas = document.createElement("canvas");
  canvas.className = "block w-full";
  const textHost = document.createElement("div");
  textHost.className = "textLayer";
  wrapper.append(canvas, textHost);
  host.append(wrapper);
  return { proxy, text, wrapper, canvas, textHost, task: null, textLayer: null };
}
